/**
 * Ahrefs Integration Module (Story 20.1)
 *
 * Barrel export + side-effect adapter registration.
 *
 * Registration happens here — NOT in `ahrefs.ts` — to prevent the Failure 5a
 * anti-pattern documented in `google/index.ts:7`. Importing this module is
 * sufficient to register the adapter; importing `ahrefs.ts` directly would
 * give you the adapter object without registration.
 */

import { registerAdapter } from "../registry";
import { ahrefsAdapter } from "./ahrefs";

// Side-effect registration (Story 20.1)
registerAdapter("ahrefs", ahrefsAdapter);

// Public surface
export {
  ahrefsAdapter,
  AHREFS_PLATFORM_VALUES,
  AHREFS_METRICS,
  AHREFS_DIMENSIONS,
  AHREFS_API_VERSION,
  AHREFS_API_BASE,
  AHREFS_BATCH_ANALYSIS_DEFAULT_SELECT,
  // Story 20.2a — exported for tests
  computeAutonomousMetricsHistoryDateFrom,
  // Story 20.9 — cross-SEO snapshot demo constants
  AHREFS_DEMO_SNAPSHOT_TARGET,
  AHREFS_DEMO_SNAPSHOT_ROW_COUNT,
  type AhrefsPlatform,
} from "./ahrefs";

// API helpers
export {
  buildAhrefsHeaders,
  parseAhrefsCostHeaders,
  fetchAhrefsDomainRating,
  fetchAhrefsBacklinksStats,
  fetchAhrefsMetrics,
  fetchAhrefsBatchAnalysis,
  validateAhrefsConnection,
  setAhrefsLogSink,
  resetAhrefsLogSink,
  // Story 20.2 — endpoint enum + 4 new helpers + 1 discovery helper
  ahrefsEndpointPath,
  fetchAhrefsEndpoint,
  fetchAhrefsKeywordsExplorerOverview,
  KEYWORDS_EXPLORER_OVERVIEW_DEFAULT_SELECT,
  // Story 35.8 — opt-in fields (NOT promoted to default-select due to 10u
  // per-keyword surcharge); surfaced via Story 20.4 widget config UI.
  KEYWORDS_EXPLORER_OVERVIEW_OPT_IN_FIELDS,
  fetchAhrefsOrganicKeywords,
  ORGANIC_KEYWORDS_DEFAULT_SELECT,
  ORGANIC_KEYWORDS_ADAPTER_LIMIT_CAP,
  fetchAhrefsRankTrackerCompetitorsStats,
  RANK_TRACKER_COMPETITORS_STATS_DEFAULT_SELECT,
  fetchAhrefsRankTrackerProjects,
  fetchAhrefsKeywordsHistory,
  KEYWORDS_HISTORY_DEFAULT_SELECT,
  KEYWORDS_HISTORY_MAX_RANGE_MONTHS,
  // Story 20.2a — time-series + per-country breakdown helpers
  fetchAhrefsMetricsHistory,
  METRICS_HISTORY_DEFAULT_SELECT,
  METRICS_HISTORY_MAX_RANGE_MONTHS,
  fetchAhrefsMetricsByCountry,
  METRICS_BY_COUNTRY_DEFAULT_SELECT,
  // Story 20.2c — per-keyword Rank Tracker `/overview` (FREE)
  fetchAhrefsRankTrackerOverview,
  RANK_TRACKER_OVERVIEW_DEFAULT_SELECT,
  RANK_TRACKER_OVERVIEW_DEFAULT_LIMIT,
  // Story 20.2d — Site Audit family (4 endpoints)
  fetchAhrefsSiteAuditProjects,
  fetchAhrefsSiteAuditIssues,
  fetchAhrefsSiteAuditPageExplorer,
  fetchAhrefsSiteAuditPageContent,
  SITE_AUDIT_PROJECTS_DEFAULT_SELECT,
  SITE_AUDIT_ISSUES_DEFAULT_SELECT,
  SITE_AUDIT_HARD_LIMIT,
  // Story 20.2e — Brand Radar family (8 endpoints, 9 _reportType discriminators)
  fetchAhrefsBrandRadarAiResponses,
  fetchAhrefsBrandRadarImpressionsHistory,
  fetchAhrefsBrandRadarMentionsHistory,
  fetchAhrefsBrandRadarImpressionsOverview,
  fetchAhrefsBrandRadarMentionsOverview,
  fetchAhrefsBrandRadarSovOverview,
  fetchAhrefsBrandRadarSovHistory,
  fetchAhrefsBrandRadarCitedDomains,
  fetchAhrefsBrandRadarCitedPages,
  BRAND_RADAR_ALL_DATA_SOURCES,
  BRAND_RADAR_FIELD_COSTS,
  BRAND_RADAR_AI_RESPONSES_DEFAULT_SELECT,
  BRAND_RADAR_OVERVIEW_DEFAULT_SELECT,
  BRAND_RADAR_CITED_DOMAINS_DEFAULT_SELECT,
  BRAND_RADAR_CITED_PAGES_DEFAULT_SELECT,
  computeBrandRadarSelectCost,
  computeBrandRadarWhereSurcharge,
  brandRadarUnitsAttributable,
  BRAND_RADAR_DATE_REGEX,
  // Story 20.2f — Web Analytics + Management discovery
  fetchAhrefsWebAnalyticsStats,
  fetchAhrefsWebAnalyticsChart,
  fetchAhrefsManagementProjects,
  filterAhrefsProjectsWithWebAnalytics,
  WEB_ANALYTICS_GRANULARITY_VALUES,
  // Story 20.2f Code Review Pass 3 (M-P3-3) — shared date validator for the
  // proxy-route layer (single source of truth — no helper/route drift).
  isValidWebAnalyticsIsoDate,
  // Story 35.8 — Subscription Info `/limits-and-usage` helper (FREE).
  fetchAhrefsSubscriptionInfoLimitsAndUsage,
  type SubscriptionInfoLimitsAndUsageParams,
  type AhrefsCallResult,
  type AhrefsCostHeaders,
  type AhrefsCallLogContext,
  type AhrefsCallLogEntry,
  type AhrefsSubscriptionInfo,
  type AhrefsEndpoint,
  type AhrefsKeywordsExplorerTargetMode,
  type KeywordsExplorerOverviewParams,
  type AhrefsOrganicKeywordsMode,
  type OrganicKeywordsParams,
  type RankTrackerCompetitorsStatsParams,
  type AhrefsRankTrackerProject,
  type KeywordsHistoryParams,
  type MetricsHistoryParams,
  type MetricsByCountryParams,
  type RankTrackerOverviewParams,
  type SiteAuditProjectsParams,
  type SiteAuditIssuesParams,
  type SiteAuditPageExplorerParams,
  type SiteAuditPageContentParams,
  type AhrefsSiteAuditProjectRow,
  // Story 20.2e — Brand Radar param types
  type BrandRadarDataSource,
  type BrandRadarAiResponsesParams,
  type BrandRadarImpressionsHistoryParams,
  type BrandRadarMentionsHistoryParams,
  type BrandRadarImpressionsOverviewParams,
  type BrandRadarMentionsOverviewParams,
  type BrandRadarSovOverviewParams,
  type BrandRadarSovHistoryParams,
  type BrandRadarCitedDomainsParams,
  type BrandRadarCitedPagesParams,
  // Story 20.2f — Web Analytics + Management interfaces
  type WebAnalyticsStatsParams,
  type WebAnalyticsChartParams,
  type WebAnalyticsGranularity,
  type AhrefsManagementProject,
} from "./api";

// Story 20.2 — cache policy
export {
  AHREFS_ENDPOINT_CACHE_TTL,
  getAhrefsCacheTtl,
  canonicalJsonStringify,
  canonicalJsonHash,
  buildAhrefsEndpointCacheKey,
} from "./cache-policy";

// Error mapping
export {
  parseAhrefsApiError,
  ahrefsErrorTypeToAdapterCode,
  parseAhrefsRetryAfter,
  sanitizeAhrefsErrorMessage,
  AhrefsApiCallError,
  // Code Review Pass 3 (P3-L7) — re-export so the discoverEndpoints probe
  // (and any future BR-403 detection sites) share a single source of truth.
  BRAND_RADAR_SUBSCRIPTION_REGEX,
  type AhrefsApiError,
  type AhrefsErrorType,
} from "./errors";

// Normalization + mode inference
export {
  normalizeAhrefsTarget,
  inferAhrefsMode,
  AhrefsTargetValidationError,
  type AhrefsMode,
} from "./normalize";

// Transformers
export {
  transformAhrefsCurrencyField,
  transformAhrefsDomainRatingResponse,
  transformAhrefsBacklinksStatsResponse,
  transformAhrefsMetricsResponse,
  transformAhrefsBatchAnalysisRow,
  transformAhrefsBatchAnalysisResponse,
  // Story 20.2 — 4 new transformers
  transformAhrefsKeywordsExplorerOverviewRow,
  transformAhrefsKeywordsExplorerOverviewResponse,
  transformAhrefsOrganicKeywordsRow,
  transformAhrefsOrganicKeywordsResponse,
  transformAhrefsRankTrackerCompetitorsStatsRow,
  transformAhrefsRankTrackerCompetitorsStatsResponse,
  transformAhrefsKeywordsHistoryRow,
  transformAhrefsKeywordsHistoryResponse,
  // Story 20.2a — 2 new transformer pairs
  transformAhrefsMetricsHistoryRow,
  transformAhrefsMetricsHistoryResponse,
  transformAhrefsMetricsByCountryRow,
  transformAhrefsMetricsByCountryResponse,
  // Story 20.2c — Rank Tracker per-keyword `/overview`
  transformAhrefsRankTrackerOverviewRow,
  transformAhrefsRankTrackerOverviewResponse,
  computePositionMomentum,
  computeAppearedToday,
  // Story 20.2d — Site Audit transformers
  transformAhrefsSiteAuditProjectRow,
  transformAhrefsSiteAuditProjectsResponse,
  transformAhrefsSiteAuditIssueRow,
  transformAhrefsSiteAuditIssuesResponse,
  transformAhrefsSiteAuditPageExplorerRow,
  transformAhrefsSiteAuditPageExplorerResponse,
  transformAhrefsSiteAuditPageContentResponse,
  // Story 20.2e — Brand Radar transformers (9 transformer pairs because
  // /sov-overview and /sov-history split into 2 distinct surfaces)
  transformAhrefsBrandRadarAiResponsesRow,
  transformAhrefsBrandRadarAiResponsesResponse,
  transformAhrefsBrandRadarImpressionsHistoryRow,
  transformAhrefsBrandRadarImpressionsHistoryResponse,
  transformAhrefsBrandRadarMentionsHistoryRow,
  transformAhrefsBrandRadarMentionsHistoryResponse,
  transformAhrefsBrandRadarImpressionsOverviewRow,
  transformAhrefsBrandRadarImpressionsOverviewResponse,
  transformAhrefsBrandRadarMentionsOverviewRow,
  transformAhrefsBrandRadarMentionsOverviewResponse,
  transformAhrefsBrandRadarSovOverviewRow,
  transformAhrefsBrandRadarSovOverviewResponse,
  transformAhrefsBrandRadarSovHistoryRow,
  transformAhrefsBrandRadarSovHistoryResponse,
  transformAhrefsBrandRadarCitedDomainRow,
  transformAhrefsBrandRadarCitedDomainsResponse,
  transformAhrefsBrandRadarCitedPageRow,
  transformAhrefsBrandRadarCitedPagesResponse,
  // Story 20.2f — Web Analytics transformers
  transformAhrefsWebAnalyticsStatsResponse,
  transformAhrefsWebAnalyticsChartRow,
  transformAhrefsWebAnalyticsChartResponse,
  // Story 35.8 — Subscription Info `/limits-and-usage` transformer (FREE).
  transformAhrefsSubscriptionInfoLimitsAndUsageResponse,
  type AhrefsReportType,
} from "./transformers";

// Unit budget tracking
export {
  AHREFS_DAILY_TENANT_UNIT_BUDGET,
  AHREFS_UNITS_KV_TTL_SEC,
  buildAhrefsIntegrationUnitsKey,
  buildAhrefsTenantUnitsKey,
  buildAhrefsTenantBreachMarkerKey,
  todayUtcYyyyMmDd,
  incrementAhrefsUnitsUsed,
  getAhrefsIntegrationDailyUnits,
  getAhrefsTenantDailyUnits,
  checkAhrefsUnitBudget,
  type AhrefsBudgetCheckResult,
} from "./unit-budget";

// Story 20.2 — endpoint discovery type re-export from adapter
export type { EndpointDiscoveryInfo } from "../adapter";
